/**
 * Circulation sur le réseau routier (E§1.8).
 *
 * Les routes vides font un décor ; les routes parcourues font une ville. C'est le
 * mouvement, et non la densité de bâtiments, qui donne l'impression que quelqu'un
 * habite là.
 *
 * ══ Ce que cette version corrige ═════════════════════════════════════════════
 *
 * La précédente faisait glisser des sprites sur un itinéraire aléatoire, au milieu de la
 * chaussée, sans que rien ne s'arrête jamais. Trois défauts, tous visibles :
 *
 * · **Le sens de marche était faux une fois sur deux.** L'indice de la direction servait
 *   d'indice de rotation du sprite, ce qui applique la réflexion `D ↦ (base − D)` : juste
 *   sur deux directions, faux sur les deux autres, pour toujours. Les voitures roulaient
 *   donc en marche arrière la moitié du temps. Voir `facing.ts`.
 * · **Tout le monde roulait sur l'axe de la rue**, donc deux véhicules en sens inverse se
 *   traversaient au lieu de se croiser.
 * · **Aucune priorité.** Les carrefours n'existaient pas : on les franchissait sans rien
 *   regarder, à quatre voitures à la fois.
 *
 * ══ Trois décisions conservées de la version précédente ══════════════════════
 *
 * **Les véhicules ne connaissent pas le réseau, ils suivent un ITINÉRAIRE calculé une
 * fois.** Recalculer un chemin à chaque frame coûterait un parcours de graphe par véhicule
 * et par image, pour un résultat identique.
 *
 * **Le nombre de véhicules suit la taille du réseau, avec un plafond.** E§3 est
 * catégorique : le juice ne coûte jamais de fps.
 *
 * **Les sprites sont enfants DIRECTS du calque trié.** Un conteneur propre au trafic n'a
 * qu'un `zIndex`, et tout le trafic passait sous toutes les tuiles opaques.
 *
 * ══ Ce que la logique pure décide, et ce que ce fichier décide ═══════════════
 *
 * `src/core/traffic.ts` détient les RÈGLES — quel carrefour porte un feu, quel axe est au
 * vert, combien de temps on marque un stop, de quelle fraction de case on se décale pour
 * rouler à droite. Elles sont testables sans navigateur, et elles le sont.
 * Ce fichier ne fait qu'obéir : il place, oriente et trie des sprites.
 */

import { Sprite } from 'pixi.js';
import type { Container } from 'pixi.js';
import type { Texture } from 'pixi.js';

import { connectionMask, roadKey } from '@/core/roads';
import { LANE_OFFSET, controlFor, greenAxis, majorAxisOf, mayEnter } from '@/core/traffic';
import type { Axis, Control } from '@/core/traffic';

import { cellToWorld } from './isoEngine';
import { rotationTowards } from './facing';
import { SYNTY_TILE } from './tileGeometry';

/**
 * Plafond dur. Au-delà, la circulation devient du bruit visuel et coûte des fps.
 *
 * Relevé de 8 à 40 (demande de Benoit, 18 août : « plus il y a de routes, plus tu
 * ajoutes de véhicules », puis « plus de véhicules en pic si possible »). Un véhicule
 * est UN sprite sans filtre : le suivi de file lui-même reste négligeable (40²
 * comparaisons par frame). La densité fait le reste : le plafond n'est atteint qu'à
 * 200 cases de route.
 */
const MAX_VEHICLES = 40;

/** Cases par seconde. Assez lent pour être suivi des yeux, assez vif pour vivre. */
const SPEED = 0.9;

/** Une case de route sur combien, au maximum, porte un véhicule. */
const DENSITY = 0.2;

/** Durée du fondu d'apparition et de disparition, en millisecondes. */
const FADE_MS = 260;

/**
 * Position de la ligne d'arrêt dans la case qui précède le carrefour, en fraction.
 *
 * 0,78 place le pare-chocs au bord de l'intersection sans mordre dessus. Plus près, les
 * véhicules arrêtés recouvrent le marquage ; plus loin, ils ont l'air de s'arrêter pour
 * rien au milieu de la rue.
 */
const STOP_LINE = 0.78;

/**
 * Distance sur laquelle un véhicule ralentit avant sa ligne d'arrêt, en cases.
 *
 * Sans elle, une voiture passe de sa vitesse de croisière à zéro entre deux images, ce qui
 * se lit comme un défaut d'affichage et non comme un freinage.
 */
const BRAKE_DISTANCE = 0.45;

/** Écart minimal entre deux véhicules qui se suivent, en cases. */
const FOLLOW_GAP = 0.62;

interface Cell {
  col: number;
  row: number;
}

interface Vehicle {
  readonly sprite: Sprite;
  /** Indice du type dans la table de textures — sert à retrouver l'asset et sa base. */
  readonly kind: number;
  /**
   * Avancement du fondu, de 0 à 1.
   *
   * Une voiture qui surgit à pleine opacité au milieu d'une rue se lit comme un défaut
   * d'affichage, pas comme du trafic. Le fondu est ce qui transforme un peuplement
   * algorithmique en circulation : on ne voit plus l'apparition, on voit une voiture qui
   * arrive.
   */
  fade: number;
  /** Vrai quand le véhicule s'efface : le fondu joue alors à l'envers. */
  leaving: boolean;
  /** Itinéraire, en cases. Calculé à l'apparition, jamais pendant le trajet. */
  readonly path: readonly Cell[];
  /** Avancement, en cases parcourues. */
  progress: number;
  /** Instant d'immobilisation, `null` tant que le véhicule roule. */
  stopSince: number | null;
  /** Direction d'ÉTAT courante, 0 nord … 3 ouest. Relue par le suivi de file. */
  direction: number;
  /** Position continue en cases d'ÉTAT — sert au suivi de file, pas à l'affichage. */
  at: { col: number; row: number };
}

export interface VehicleKind {
  /** Nom d'asset, pour retrouver son orientation de base mesurée. */
  readonly asset: string;
  /** Les quatre rotations rendues, dans l'ordre du pipeline. */
  readonly frames: readonly Texture[];
}

/** Direction d'ÉTAT d'un pas entre deux cases voisines. */
function directionOf(from: Cell, to: Cell): number {
  if (to.row < from.row) return 0;
  if (to.col > from.col) return 1;
  if (to.row > from.row) return 2;
  return 3;
}

/**
 * Vecteur « à droite du sens de marche », en cases.
 *
 * Sur une carte dont `row` croît vers le sud, aller vers l'est met le sud à sa droite —
 * d'où la rotation d'un quart de tour appliquée ici. Le calcul se fait en cases et non en
 * pixels : exprimer la voie en pixels la ferait dériver au premier changement de géométrie
 * de tuile.
 */
function rightOf(direction: number): Cell {
  switch (direction) {
    case 0:
      return { col: 1, row: 0 };
    case 1:
      return { col: 0, row: 1 };
    case 2:
      return { col: -1, row: 0 };
    default:
      return { col: 0, row: -1 };
  }
}

export class Traffic {
  private readonly vehicles: Vehicle[] = [];
  private roads: Cell[] = [];

  /**
   * Multiplicateur de densité — C.3, la page visiteur.
   *
   * La vitrine (E§5) montre un trafic DENSE : la première impression doit être une
   * ville qui vit, pas une ville qui dort. Le plafond `MAX_VEHICLES`, lui, ne bouge
   * jamais : c'est une borne de fps (E§3), pas un réglage d'ambiance.
   */
  private densityBoost = 1;

  setDensityBoost(multiplier: number): void {
    this.densityBoost = Math.max(1, multiplier);
  }
  private adjacency = new Map<string, Cell[]>();
  /** Contrôle applicable à chaque case, dérivé du masque. Recalculé avec le réseau. */
  private controls = new Map<string, { control: Control; majorAxis: Axis | null }>();

  /**
   * `toView` projette une case de l'état de jeu vers la case affichée. Le trafic doit
   * l'appliquer comme le reste : sans cela, les voitures rouleraient à côté des routes
   * dès le premier quart de tour.
   *
   * `layer` est le calque TRIÉ du rendu, pas un conteneur à nous — surtout pas un
   * `zIndex` élevé, qui ferait passer les voitures devant les bâtiments.
   */
  constructor(
    private readonly kinds: readonly VehicleKind[],
    private readonly layer: Container,
  ) {}

  /**
   * Reconstruit la table d'adjacence et les contrôles du réseau.
   *
   * Appelé quand le réseau change, jamais par frame : c'est la seule opération de graphe
   * du système. Les contrôles sont DÉRIVÉS du masque plutôt que mémorisés — une table de
   * feux tenue à part divergerait du tracé au premier élagage de rue.
   */
  setNetwork(roadKeys: readonly string[]): void {
    this.roads = roadKeys.map((key) => {
      const [col, row] = key.split(':');
      return { col: Number(col), row: Number(row) };
    });

    const present = new Set(roadKeys);
    this.adjacency = new Map();
    this.controls = new Map();

    for (const cell of this.roads) {
      const neighbours: Cell[] = [];
      for (const [dc, dr] of [
        [0, -1],
        [1, 0],
        [0, 1],
        [-1, 0],
      ]) {
        const col = cell.col + (dc ?? 0);
        const row = cell.row + (dr ?? 0);
        if (present.has(roadKey(col, row))) neighbours.push({ col, row });
      }
      const key = roadKey(cell.col, cell.row);
      this.adjacency.set(key, neighbours);

      const mask = connectionMask(present, cell.col, cell.row);
      this.controls.set(key, {
        control: controlFor(mask, cell.col, cell.row),
        majorAxis: majorAxisOf(mask),
      });
    }

    // Un véhicule dont la route a disparu — vendue, déplacée — s'efface plutôt que de
    // rouler dans le vide.
    for (const vehicle of this.vehicles) {
      const alive = vehicle.path.every((cell) => present.has(roadKey(cell.col, cell.row)));
      // Fondu de sortie plutôt que disparition sèche : la voiture quitte la scène.
      if (!alive) vehicle.leaving = true;
    }
  }

  /** Marche aléatoire sur le réseau : simple, et suffisant pour lire comme du trafic. */
  private makePath(start: Cell, length: number): Cell[] {
    const path = [start];
    let previous: string | null = null;
    let current = start;

    for (let step = 0; step < length; step++) {
      const key = roadKey(current.col, current.row);
      const options = (this.adjacency.get(key) ?? []).filter(
        (cell) => roadKey(cell.col, cell.row) !== previous,
      );
      // Impasse : on autorise le demi-tour plutôt que d'arrêter le véhicule net.
      const pool = options.length > 0 ? options : (this.adjacency.get(key) ?? []);
      const next = pool[Math.floor(Math.random() * pool.length)];
      if (next === undefined) break;

      previous = key;
      current = next;
      path.push(next);
    }

    return path;
  }

  private spawn(): void {
    if (this.roads.length < 2) return;

    const start = this.roads[Math.floor(Math.random() * this.roads.length)];
    if (start === undefined) return;

    const path = this.makePath(start, 6 + Math.floor(Math.random() * 8));
    if (path.length < 2) return;

    const kind = Math.floor(Math.random() * this.kinds.length);
    const frames = this.kinds[kind]?.frames;
    if (frames?.[0] === undefined) return;

    const sprite = new Sprite(frames[0]);
    sprite.anchor.set(0.5, 0.5);
    // Nettement plus petits que les bâtiments : une voiture à l'échelle du modèle
    // remplirait sa case et se lirait comme un bâtiment de plus. 0,35 et non 0,42 :
    // à 0,42, deux carrosseries qui se croisaient sur leurs voies se chevauchaient
    // (« superposition », Benoit, 18 août) — la chaussée ne fait que 0,38 case.
    sprite.scale.set(0.35);
    // Naît invisible : le fondu la fait entrer dès la première frame.
    sprite.alpha = 0;
    this.layer.addChild(sprite);

    this.vehicles.push({
      sprite,
      path,
      progress: 0,
      kind,
      fade: 0,
      leaving: false,
      stopSince: null,
      direction: directionOf(path[0] ?? start, path[1] ?? start),
      at: { col: start.col, row: start.row },
    });
  }

  private retire(index: number): void {
    const vehicle = this.vehicles[index];
    if (vehicle === undefined) return;
    this.layer.removeChild(vehicle.sprite);
    vehicle.sprite.destroy();
    this.vehicles.splice(index, 1);
  }

  /**
   * Avancement maximal autorisé par le carrefour qui suit, ou `Infinity`.
   *
   * Le véhicule s'arrête AVANT d'entrer dans la case contrôlée, jamais dedans : un
   * véhicule immobilisé au milieu d'un carrefour bloquerait l'axe perpendiculaire, ce qui
   * est le contraire d'une priorité.
   */
  private gateFor(vehicle: Vehicle, index: number, now: number): number {
    const next = vehicle.path[index + 1];
    if (next === undefined) return Infinity;

    /*
      ── On ne s'arrête JAMAIS dans un carrefour ────────────────────────────────

      Quand deux carrefours se suivent, la ligne d'arrêt du second tombe AU MILIEU
      du premier : le véhicule marquait son stop garé en plein croisement — « les
      véhicules restent bloqués dans les intersections » (rapport de Benoit). La
      règle du monde réel est la même que la nôtre : on n'entre pas dans un
      croisement sans pouvoir en sortir, mais une fois dedans, ON LE DÉGAGE. Si la
      case foulée est elle-même contrôlée, la barrière suivante ne retient pas.
    */
    const from = vehicle.path[index];
    if (from !== undefined && this.controls.get(roadKey(from.col, from.row))?.control != null) {
      return Infinity;
    }

    const gate = this.controls.get(roadKey(next.col, next.row));
    if (gate?.control == null) return Infinity;

    const allowed = mayEnter({
      control: gate.control,
      col: next.col,
      row: next.row,
      direction: vehicle.direction,
      now,
      stopSince: vehicle.stopSince,
      majorAxis: gate.majorAxis,
    });

    /*
      Un carrefour OCCUPÉ ne se traverse pas, même au vert ou après son stop :
      deux véhicules d'axes croisés se superposaient au milieu du croisement
      (« superposition », Benoit, 18 août). L'attente est transitoire par
      construction — personne ne s'ARRÊTE jamais dans un carrefour (règle
      ci-dessus), donc l'occupant en sort toujours.

      Et elle est BORNÉE : deux files en sens inverse entre deux carrefours
      voisins peuvent théoriquement former une attente circulaire. Au-delà de
      quatre secondes d'arrêt complet, on passe — une superposition d'une
      seconde vaut mieux qu'un blocage définitif, qui est exactement le défaut
      que ce fichier corrige.
    */
    const patience = vehicle.stopSince !== null && now - vehicle.stopSince > 4000;
    if (allowed && !patience && this.junctionBusy(next, vehicle)) return index + STOP_LINE;

    return allowed ? Infinity : index + STOP_LINE;
  }

  /**
   * Un autre véhicule se tient-il AU CŒUR de cette case de carrefour ?
   *
   * ⚠️ Le rayon doit rester SOUS 0,22 : un véhicule arrêté à sa ligne d'arrêt
   * (`STOP_LINE` = 0,78) se tient à 0,22 case du centre du carrefour. Un premier
   * rayon de 0,5 l'englobait — deux véhicules arrêtés face à face se voyaient
   * mutuellement « dans le carrefour » et s'attendaient l'un l'autre : 9 véhicules
   * figés 36 s, vus ROUGES par la sonde avant d'être compris ici.
   */
  private junctionBusy(cell: Cell, vehicle: Vehicle): boolean {
    for (const other of this.vehicles) {
      if (other === vehicle || other.leaving) continue;
      if (Math.abs(other.at.col - cell.col) < 0.2 && Math.abs(other.at.row - cell.row) < 0.2) {
        return true;
      }
    }
    return false;
  }

  /**
   * Avancement maximal imposé par le véhicule qui précède, ou `Infinity`.
   *
   * Seuls les véhicules allant dans la MÊME direction font obstacle. Le sens inverse
   * roule sur l'autre voie et ne bloque personne ; les axes croisés sont réglés par les
   * feux. Sans cette restriction, deux voitures se faisant face se bloqueraient
   * mutuellement pour toujours au milieu d'une rue.
   */
  private followGap(vehicle: Vehicle): number {
    let cap = Infinity;

    for (const other of this.vehicles) {
      if (other === vehicle || other.leaving) continue;
      if (other.direction !== vehicle.direction) continue;

      const dc = other.at.col - vehicle.at.col;
      const dr = other.at.row - vehicle.at.row;
      // Écart le long du sens de marche : négatif si l'autre est derrière.
      const ahead =
        vehicle.direction === 0
          ? -dr
          : vehicle.direction === 1
            ? dc
            : vehicle.direction === 2
              ? dr
              : -dc;
      const lateral =
        vehicle.direction === 0 || vehicle.direction === 2 ? Math.abs(dc) : Math.abs(dr);

      if (ahead <= 0 || ahead > FOLLOW_GAP || lateral > 0.5) continue;
      cap = Math.min(cap, vehicle.progress + (ahead - FOLLOW_GAP));
    }

    return cap;
  }

  update(deltaMs: number, now: number): void {
    /*
      Un PLANCHER d'un véhicule dès que le réseau en compte trois cases.

      La densité seule donnait zéro voiture sur les petits réseaux — `floor(4 × 0,12)`
      vaut 0 — donc la circulation n'apparaissait qu'après une trentaine de cases de
      route, c'est-à-dire des heures de jeu. Or c'est justement au début que la ville a
      le plus besoin de paraître habitée.
    */
    const target =
      this.roads.length < 3
        ? 0
        : Math.max(
            1,
            Math.min(MAX_VEHICLES, Math.floor(this.roads.length * DENSITY * this.densityBoost)),
          );
    if (this.vehicles.length < target) this.spawn();

    const step = (deltaMs / 1000) * SPEED;

    for (let i = this.vehicles.length - 1; i >= 0; i--) {
      const vehicle = this.vehicles[i];
      if (vehicle === undefined) continue;

      /*
        Le fondu avance dans les DEUX sens selon `leaving`. Une voiture dont l'itinéraire
        s'achève, ou dont la route a été vendue, s'efface en 260 ms au lieu de s'évaporer
        entre deux images.
      */
      vehicle.fade = Math.max(
        0,
        Math.min(1, vehicle.fade + (vehicle.leaving ? -1 : 1) * (deltaMs / FADE_MS)),
      );
      vehicle.sprite.alpha = vehicle.fade;

      if (vehicle.leaving && vehicle.fade <= 0) {
        this.retire(i);
        continue;
      }

      // Une voiture qui s'en va ne roule plus : elle finit son mouvement en fondu.
      if (vehicle.leaving) continue;

      const index = Math.floor(vehicle.progress);

      // Fin d'itinéraire : la voiture s'efface en douceur au lieu de s'évaporer.
      if (index >= vehicle.path.length - 1) {
        vehicle.leaving = true;
        continue;
      }

      const from = vehicle.path[index];
      const to = vehicle.path[index + 1];
      if (from === undefined || to === undefined) {
        this.retire(i);
        continue;
      }

      vehicle.direction = directionOf(from, to);

      /*
        ── Freinage, arrêt, redémarrage ────────────────────────────────────────

        Le plafond d'avancement est le plus contraignant des deux : le carrefour qui suit
        et le véhicule qui précède. On s'en approche en RALENTISSANT, sur `BRAKE_DISTANCE`,
        parce qu'un passage de la vitesse de croisière à zéro entre deux images se lit
        comme un bug d'affichage et non comme un freinage.
      */
      const cap = Math.min(this.gateFor(vehicle, index, now), this.followGap(vehicle));
      const room = cap - vehicle.progress;
      const slow = room >= BRAKE_DISTANCE ? 1 : Math.max(0, room / BRAKE_DISTANCE);
      vehicle.progress = Math.min(cap, vehicle.progress + step * slow);

      // `stopSince` n'a de sens qu'à l'arrêt complet : c'est lui qui fait repartir d'un
      // stop après le délai réglementaire, et il doit se remettre à zéro au redémarrage.
      const halted = room <= 0.01;
      if (halted && vehicle.stopSince === null) vehicle.stopSince = now;
      else if (!halted && room > BRAKE_DISTANCE) vehicle.stopSince = null;

      const t = vehicle.progress - index;
      vehicle.at = {
        col: from.col + (to.col - from.col) * t,
        row: from.row + (to.row - from.row) * t,
      };

      /*
        ── On roule à DROITE ───────────────────────────────────────────────────

        Le décalage se calcule en cases puis se projette, comme tout le reste : la
        projection est isométrique, donc un pas latéral d'un dixième de case ne vaut ni
        (0, −0,1) ni un nombre de pixels fixe.
      */
      const viewFrom = from;
      const viewTo = to;
      const viewDirection = directionOf(viewFrom, viewTo);
      const lane = rightOf(viewDirection);

      const a = cellToWorld(viewFrom, SYNTY_TILE);
      const b = cellToWorld(viewTo, SYNTY_TILE);
      const offset = cellToWorld(
        { col: lane.col * LANE_OFFSET, row: lane.row * LANE_OFFSET },
        SYNTY_TILE,
      );

      vehicle.sprite.position.set(
        a.x + (b.x - a.x) * t + offset.x,
        a.y + (b.y - a.y) * t + offset.y,
      );

      /*
        ── UN VÉHICULE SE TRIE OÙ IL EST, PAS OÙ IL ÉTAIT ─────────────────────

        La profondeur se calculait sur `viewFrom`, la case QUITTÉE. Un véhicule aux trois
        quarts du chemin se tenait donc physiquement sur la case suivante tout en étant
        trié sur la précédente — et la tuile de route de la case suivante, de profondeur
        supérieure d'un cran, se peignait par-dessus lui.

        Résultat à l'écran, rapporté par le joueur : « les voitures passent sous certaines
        routes ». Une fois par case franchie, sur la moitié aval du trajet.

        On trie sur la case que le véhicule FOULE, obtenue en arrondissant sa position
        interpolée — décalage de voie compris. L'arrondi n'est pas un détail : une
        profondeur continue ne suffit pas. Une tuile vaut deux crans de profondeur, un
        véhicule n'en gagne que 0,85 sur la sienne, et une profondeur continue le laissait
        donc encore 0,65 SOUS la tuile suivante pendant tout le tiers aval de la case —
        mesuré par la sonde, qui est restée rouge sur cette première correction.

        Arrondi, le véhicule est toujours exactement 0,35 au-dessus de sa propre tuile et
        1,65 sous la suivante, et la bascule tombe pile au franchissement de la frontière.
      */
      const laneCol = viewFrom.col + (viewTo.col - viewFrom.col) * t + lane.col * LANE_OFFSET;
      const laneRow = viewFrom.row + (viewTo.row - viewFrom.row) * t + lane.row * LANE_OFFSET;
      vehicle.sprite.zIndex = (Math.round(laneCol) + Math.round(laneRow)) * 2 + 0.85;

      /*
        ── L'orientation suit le sens de marche, PAR LA LOI ────────────────────

        Le sens se lit dans le repère de la VUE : après un quart de tour, une voiture qui
        va vers l'est de l'état va vers le sud de l'écran, et son sprite doit suivre.

        `rotationTowards` applique la loi mesurée `(base − cible) mod 4`. Le code passait
        auparavant la direction directement comme indice de texture, ce qui faisait rouler
        en marche arrière exactement deux directions sur quatre.
      */
      const kind = this.kinds[vehicle.kind];
      if (kind !== undefined) {
        const rotation = rotationTowards(kind.asset, viewDirection) ?? 0;
        const frame = kind.frames[rotation] ?? kind.frames[0];
        if (frame !== undefined && vehicle.sprite.texture !== frame) {
          vehicle.sprite.texture = frame;
        }
      }
    }
  }

  /**
   * Les carrefours qui portent un feu, avec l'axe au vert — pour l'affichage.
   *
   * Le trafic est le seul à connaître le réseau et ses contrôles ; publier cette liste
   * évite au moteur de rendu de recalculer les masques une deuxième fois, et garantit que
   * ce que le joueur voit au feu est ce que les véhicules obéissent.
   */
  lights(now: number): { cell: Cell; green: Axis | null }[] {
    const found: { cell: Cell; green: Axis | null }[] = [];
    for (const cell of this.roads) {
      const gate = this.controls.get(roadKey(cell.col, cell.row));
      if (gate?.control?.kind !== 'light') continue;
      found.push({ cell, green: greenAxis(cell.col, cell.row, now) });
    }
    return found;
  }

  destroy(): void {
    // Le calque ne nous appartient PAS : on ne détruit que nos propres sprites.
    // Le détruire emporterait tuiles, routes, props et bâtiments avec lui — c'est
    // exactement le défaut du `removeChildren()` sur conteneur partagé déjà vécu (§8).
    for (const vehicle of this.vehicles) {
      this.layer.removeChild(vehicle.sprite);
      if (!vehicle.sprite.destroyed) vehicle.sprite.destroy();
    }
    this.vehicles.length = 0;
  }
}
